import { DisplayHandler, ExecutionCompleteArgs, PreparedElement, zIndexes, ExecutionId } from './displayHandler';
import { LaqorrFileEntry, FileUtils } from 'src/fileUtils';
import { Subject } from 'rxjs';
import { Logger } from "src/logger";
import { NgZone } from '@angular/core';
import { Rectangle } from "src/geometry";
import { calculateTizenVideoDisplayRect } from './calculateTizenVideoDisplayRect';
import { FiniteStateMachineStateManager } from 'src/finiteStateMachineStateManager';
import { LoggerService } from 'src/loggerService';
import { LocalTimeZoneClock } from 'src/clock';
import { ITizenVideoDisplayHandlerContext, ITizenVideoDisplayHandlerState, TizenVideoDisplayHandlerStateIdle } from './tizenVideoDisplayState';
import { TizenVideoReservationManager } from './tizenVideoReservationManager';
import { TizenAvPlaySharedListener } from './tizenAvPlaySharedListener';
import { TizenVideoDisplayHandlerDependencies } from './tizenVideoDisplayHandlerDependencies';

const LOG_TAG = "tizenVideoDisplayHandler";

function getAvPlayVersionNumber() : number {
  if (typeof(webapis) != 'undefined') {
    return parseFloat(webapis.avplay.getVersion());
  } else {
    return NaN;
  }
}

const rotationZero: AVPlayRotation = getAvPlayVersionNumber() >= 7.0 
  ? "PLAYER_DISPLAY_ROTATION_NONE"
  : "PLAYER_DISPLAY_ROTATION_0";

const avplayDisplayRotation: AVPlayRotation[] = [
  rotationZero,
  "PLAYER_DISPLAY_ROTATION_270",
  "PLAYER_DISPLAY_ROTATION_180",
  "PLAYER_DISPLAY_ROTATION_90"
];

function degreesToPlayerRotation(degrees: number) : AVPlayRotation {
  return avplayDisplayRotation[Math.round(degrees / 90) % 4];
}

export class TizenVideoDisplayHandler implements DisplayHandler, ITizenVideoDisplayHandlerContext {
    private readonly finiteStateMachineManager: FiniteStateMachineStateManager<ITizenVideoDisplayHandlerState>;


    private readonly avPLayPlaybackCallback: AVPlayPlaybackCallback = {
      onstreamcompleted: () => {
        this.logger.information(LOG_TAG, "onstreamcompleted event fired");
        this.ngZone.run(
          () => this.finiteStateMachineManager.transition(s => s.onStreamCompleted())
        );
      },
      onbufferingstart: () => {
        this.logger.information(LOG_TAG, "onbufferingstart event fired");
      },
      onbufferingcomplete: () => {
        this.logger.information(LOG_TAG, "onbufferingcomplete event fired");
      },
      onerror: (error:AVPlayError) => {
        const errorText = `TizenVideoDisplayHandler.playerManager.onerror triggered. { code='${error.code}', name = '${error.name}', message='${error.message}' }`;
         this.logger.error(
            'TizenVideoDisplayHandler',
            errorText,
            error
          );
          this.log(errorText);
          this.finiteStateMachineManager.transition(s => s.onError());
      }
    }

    readonly localTimeZoneClock : LocalTimeZoneClock;
    private readonly logger : Logger;
    private readonly ngZone : NgZone;
    private readonly tizenVideoReservationManager : TizenVideoReservationManager;
    private readonly tizenAvPlaySharedListener : TizenAvPlaySharedListener;
    private readonly loggerService : LoggerService;

    constructor(
      private readonly dependencies: TizenVideoDisplayHandlerDependencies
    ) {
      this.localTimeZoneClock = dependencies.localTimeZoneClock;
      this.logger = dependencies.logger;
      this.ngZone = dependencies.ngZone;
      this.tizenVideoReservationManager = dependencies.tizenVideoReservationManager;
      this.tizenAvPlaySharedListener = dependencies.tizenAvPlaySharedListener;
      this.loggerService = dependencies.loggerService;
      if(typeof(webapis) !== 'undefined') {
        this.playerManager = (webapis || { avplay: undefined}).avplay;
      }
      this.finiteStateMachineManager = new FiniteStateMachineStateManager<ITizenVideoDisplayHandlerState>(new TizenVideoDisplayHandlerStateIdle(this));
    }

    cancelVideoPlayerReservationRequest(requestId: number) {
      this.logger.debug(LOG_TAG, `tizenVideoDisplayHandler.cancelVideoPlayerReservationRequest(${requestId}) invoked`);
        this.tizenVideoReservationManager.cancelReservationRequest(requestId);
    }

    relinquishVideoPlayerReservation() {
      this.tizenAvPlaySharedListener.unsubscribe(this.avPLayPlaybackCallback);
      this.logger.debug(LOG_TAG, "tizenVideoDisplayHandler.relinquishReservation invoked");
      this.tizenVideoReservationManager.relinquishReservation();
    }

    requestVideoPlayerReservation(): number {
      this.logger.debug(LOG_TAG, "tizenVideoDisplayHandler.requestVideoPlayerReservation invoked");
      return this.tizenVideoReservationManager.requestReservation(
        (id:number) =>
        {
          this.logger.debug(LOG_TAG, `tizenVideoReservationManager.requestReservation event fired. id = ${id}`);
          try {
            this.tizenAvPlaySharedListener.subscribe(this.avPLayPlaybackCallback);
          } catch(ex) {
            this.logger.error(LOG_TAG, `error thrown when calling this.tizenAvPlaySharedListener.subscribed: ${ex}`);
          }
          this.finiteStateMachineManager.transition(
            s => s.onVideoPlayerReserved(id)
          );
        }
      );
    }

    readonly playCompleteSubject = new Subject<ExecutionCompleteArgs>();
    get mediaExecutionComplete$() {
        return this.playCompleteSubject.asObservable();
    }

    log(text: string) {
      this.logger.debug(LOG_TAG, text);
      this.ngZone.run(
        () => this.loggerService.logDebug(LOG_TAG, text)
      );
    }

    element: PreparedElement<HTMLObjectElement> = {
        file: null,
        element: null,
        display: "none",
        zIndex: zIndexes.hiding,
        opacity: 1.0
    }

    get preparedElements() {
        return [this.element];
    }

    readonly playerManager: AVPlayManager;
    stopAll() {
      this.finiteStateMachineManager.transition(state => state.onStopAllRequested());
    }

    initializeRotationAndPosition() {
      try {
        const targetRectangle = this.element.element.parentElement.getBoundingClientRect();
        const converted:Rectangle = {
            left: targetRectangle.left,
            top: targetRectangle.top,
            height: targetRectangle.height,
            width: targetRectangle.width
        };
        const rotation = degreesToPlayerRotation(this.dependencies.deviceSettings.playerSettings.screenRotation); 
        const transformed = calculateTizenVideoDisplayRect(converted, rotation);
        
        try {
          this.playerManager.setDisplayRect(
            transformed.left,
            transformed.top,
            transformed.width,
            transformed.height
          );        
        }
        catch(error) {
          this.logger.error(`TizenVideoDisplayHandler`, `Error thrown when calling this.playerManager.setDisplayRect(${transformed.left}, ${transformed.top}, ${transformed.width}, ${transformed.height}) ${error}`, error);
          this.loggerService.logError(LOG_TAG, error);
          return;
        }
        try {
          this.playerManager.setDisplayRotation(
              rotation
          );
        } catch(error) {
          this.logger.error(`TizenVideoDisplayHandler`, `Error thrown when calling this.playerManager.setDisplayRotation: ${error}`, error);
          this.loggerService.logError(LOG_TAG, error);
        }
    }
    catch(error) {
      this.logger.error(`TizenVideoDisplayHandler`, `Error thrown in initializeRotationAndPosition: ${error}`, error);
      this.loggerService.logError(LOG_TAG, error);     
    }
  }

  async playerInitialRender(mediaFile: LaqorrFileEntry): Promise<void> {
    await this.playerPrepare(mediaFile);
    this.playerManager.setVideoStillMode("false");
    this.playerManager.play();
    await new Promise(
      (resolve, reject) => {
        window.setTimeout(
          resolve,
          50
        )
      }
    );
    this.playerManager.stop();
    this.playerManager.setVideoStillMode("true")
  }

    async playerPrepare(mediaFile: LaqorrFileEntry) : Promise<void> {
      const avplayerState = this.playerManager.getState();
      // In ordinary circumstances the state should be 'IDLE'.
      // This might not be the case if the playlist changed on the fly
      // (due to a scheduled change, or a file being added or deleted)
      if(avplayerState === "READY") {
        try {
          this.playerManager.stop();
        } catch(exception) {
          this.logger.error(
            "tizenVideoDisplayHandler",
            "attempt to call stop from the ready state failed",
            exception
          );
        }
        this.logger.debug(
          "tizenVideoDisplayHandler",
          `inside playerPrepare - attempting to call close on the player. Current state is ${avplayerState}`
        );
        try {
          this.playerManager.close();
        } catch(exception) {
          this.logger.error(
            "tizenVideoDisplayHandler",
            `attempt to call close failed`,
            exception
          );
        }
      }
      this.element.file = mediaFile;
      this.element.display = "block";
      try {
        this.playerManager.open(FileUtils.getLaqorrFileEntryUrl(mediaFile));
      } catch (error) {
        this.logger.error(
          "tizenVideoDisplayHandler",
          `playerPrepare threw an error, when calling this.playerManager.open. Current player state is ${avplayerState}`, 
          error
        );
      }
      this.initializeRotationAndPosition();
      try {
        // NOTE: Do not call prepareAsync here.
        // If this mediaFile has been deleted from storage (which is possible)
        // then prepareAsync just fails without invoking a callback.
        // Instead, Calling the synchronous playerManager.prepare method will 
        // trigger the 'onError' call
        this.playerManager.prepare();
      } catch(e) {
        this.logger.error('TizenVideoDisplayHandler', `playerManager.prepare threw an exception`, e);
      }
      // Trying to figure out when best to utilize setVideoStillMode
      this.playerManager.setVideoStillMode("true");
    }

    playerPlay() {
      this.playerManager.setVideoStillMode("false");
      this.playerManager.play();
      this.ngZone.run(
        () => {
          this.element.zIndex = zIndexes.showing;
          this.element.opacity = 1.0;
          this.element.display = "block";
        }
      );
    }

    hide() {
      return new Promise<void>(
        resolve =>
        {
          // The window setTimout is to give the next item time to go to the front
          window.setTimeout(
            () => {
              this.ngZone.run(
                () => {
                  this.element.zIndex = zIndexes.hiding;
                  this.element.display = "none";   // It appears that the video display does not respect the z-index. Nor this!
                  this.element.opacity = 0.0; // So of course it's not going to respect opacity either
                  this.logger.debug(LOG_TAG, "hide. Set zIndex to hiding, and display to none")
                  resolve();
                }
              );
            },
            100
          );
        }
      );
    }

    firePlayComplete(playCompleteArgs: ExecutionCompleteArgs) {
      this.ngZone.run(
        () => this.playCompleteSubject.next(playCompleteArgs)
      );
    }

    playerStop() {
      return new Promise<void>(
        resolve =>
        {
          this.ngZone.run(
            () => {
              this.playerManager.setVideoStillMode("true");
              this.playerManager.stop();
              this.element.file = null;
              this.element.display = "block";
              this.logger.debug(LOG_TAG, "playerStop. Set videoStilMode to true, and display to block");
              resolve();
            }
          );
        }
      )
    }

    async play(mediaFile: LaqorrFileEntry, executionId: ExecutionId)  {
      this.finiteStateMachineManager.transition(
        state => state.onPlayRequested(mediaFile, executionId)
      );
    }

    prepareNext(mediaFile: LaqorrFileEntry) : void {
      this.finiteStateMachineManager.transition(
        state => state.onPrepareRequested(mediaFile)
      );
    }

    cancelPrepare(): void {
      this.finiteStateMachineManager.transition(
        state => state.onPrepareCancelled()
      );
    }
}